import React, { useState } from 'react';
import { X, Copy, CheckCircle, Code, Link, MessagesSquare, Palette } from 'lucide-react';
import Toast from './Toast';
import ComboWidgetV2ThemeCustomizer from './ComboWidgetV2ThemeCustomizer';

const COPY_LABELS = {
  url: 'URL',
  fullIframe: 'Full-page iframe',
  inlineIframe: 'Inline card iframe',
  lightweightSnippet: 'Lightweight snippet',
  floatingScript: 'Floating button script',
  openApi: 'Open-from-page example',
};

const CopyButton = ({ text, type, copySuccess, onCopy }) => (
  <button
    type="button"
    onClick={() => onCopy(text, type)}
    className="px-3 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors flex items-center gap-1.5 text-sm flex-shrink-0"
  >
    {copySuccess[type] ? (
      <><CheckCircle className="w-3.5 h-3.5" />Copied</>
    ) : (
      <><Copy className="w-3.5 h-3.5" />Copy</>
    )}
  </button>
);

const CodeBlock = ({ value }) => (
  <div className="flex-1 p-3 bg-muted/50 border border-border rounded-lg font-mono text-xs text-foreground overflow-x-auto max-h-40 overflow-y-auto">
    <pre className="whitespace-pre-wrap break-words">{value}</pre>
  </div>
);

const ComboWidgetV2EmbedModal = ({ agent, onClose }) => {
  const [copySuccess, setCopySuccess] = useState({
    url: false, fullIframe: false, inlineIframe: false, lightweightSnippet: false, floatingScript: false,
    openApi: false,
  });
  const [toast, setToast] = useState(null);
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [agentState, setAgentState] = useState(agent);

  const baseUrl = window.location.origin;
  const widgetBase = `${baseUrl}/embed/combo-widget-v2/${agentState.id}`;

  const fullIframeCode =
    `<iframe\n  src="${widgetBase}?view=full"\n  width="100%"\n  height="720"\n  frameborder="0"\n  allow="microphone"\n  style="border-radius:16px; box-shadow:0 12px 36px rgba(2,14,30,0.35);"\n></iframe>`;

  const inlineIframeCode =
    `<iframe\n  src="${widgetBase}?view=inline"\n  width="100%"\n  height="620"\n  frameborder="0"\n  allow="microphone"\n  style="border-radius:16px; box-shadow:0 8px 24px rgba(2,14,30,0.3);"\n></iframe>`;

  const floatingScriptCode =
    `<script>\n(function() {\n  var i = document.createElement('iframe');\n  i.src = '${widgetBase}?view=button';\n  i.style.cssText = 'position:fixed;bottom:0;right:0;width:min(424px, calc(100vw - 2rem));height:min(680px, calc(100vh - 2rem));border:none;z-index:999999;background:transparent;';\n  i.setAttribute('allow', 'microphone');\n  document.body.appendChild(i);\n})();\n</script>`;

  // Lightweight loader snippet (same short Tawk.to-style pattern as the Chat
  // widget's recommended embed): inserts one external <script> tag pointing at
  // the static combo-widget-v2.js loader, which mounts a launcher-sized iframe
  // and resizes/repositions it live to match the widget's real footprint. Far
  // less code on the client's page, and — unlike the raw iframe below — it never
  // reserves a big invisible rectangle over the page corner while closed, so it
  // stays fully responsive and non-blocking on desktop and mobile.
  const lightweightLoaderSrc = `${baseUrl}/combo-widget-v2.js?agent=${agentState.id}`;
  const lightweightSnippet = `<script type="text/javascript">
var BraincxCombo_API=BraincxCombo_API||{}, BraincxCombo_LoadStart=new Date();
(function(){
var s1=document.createElement("script"),s0=document.getElementsByTagName("script")[0];
s1.async=true;
s1.src='${lightweightLoaderSrc}';
s1.charset='UTF-8';
s0.parentNode.insertBefore(s1,s0);
})();
</script>`;

  // The widget lives inside the loader's iframe, so a host page cannot open it by
  // querying for the launcher button — document.querySelector cannot cross into
  // another document. The loader exposes this command API instead. Safe to call
  // before the widget has finished loading: the command is queued and replayed.
  const openApiExample = `<script>
  // Open the widget from your own button, popup, or CTA.
  window.BrainCXCombo.open();     // also: .close(), .toggle()

  // Optional: run something once the widget is mounted and ready.
  window.addEventListener('braincx-combo-ready', function () {
    console.log('widget ready:', window.BrainCXCombo.isReady());
  });
</script>`;

  const handleCopy = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(prev => ({ ...prev, [type]: true }));
      setToast({ message: `${COPY_LABELS[type]} copied!`, type: 'success' });
      setTimeout(() => setCopySuccess(prev => ({ ...prev, [type]: false })), 2000);
    } catch {
      setToast({ message: 'Failed to copy', type: 'error' });
    }
  };

  const openPreview = (view) => window.open(`${widgetBase}?view=${view}`, '_blank');

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-elegant">
        <div className="bg-gradient-secondary text-white p-5 flex justify-between items-center rounded-t-2xl flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
              <MessagesSquare className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-lg font-semibold tracking-tight">Embed Combo Widget V2: {agentState.name}</h2>
              <p className="text-white/70 text-xs mt-0.5">Flagship dark navy design — same chat + voice backend as the original Combo Widget</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white p-1.5 rounded-lg hover:bg-white/10" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-7">
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 text-sm">
            <p className="font-semibold text-primary mb-1">Same backend, new design</p>
            <p className="text-muted-foreground text-xs leading-relaxed">
              Combo Widget V2 uses the exact same chat and voice engine as the original Combo Widget —
              only the launcher, header, and panel visuals are different (dark navy shell, custom logo, and
              a first-open screen that lets visitors choose to talk or type).
            </p>
          </div>

          <div className="space-y-2.5">
            <div className="flex items-center gap-2">
              <Link className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Direct URL</h3>
            </div>
            <div className="flex gap-2">
              <div className="flex-1 p-3 bg-muted/50 border border-border rounded-lg font-mono text-sm break-all">{widgetBase}</div>
              <CopyButton text={widgetBase} type="url" copySuccess={copySuccess} onCopy={handleCopy} />
            </div>
          </div>

          <div className="space-y-2.5">
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Full-Page Embed</h3>
            </div>
            <div className="flex gap-2">
              <CodeBlock value={fullIframeCode} />
              <CopyButton text={fullIframeCode} type="fullIframe" copySuccess={copySuccess} onCopy={handleCopy} />
            </div>
          </div>

          <div className="space-y-2.5">
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Inline Card Embed</h3>
            </div>
            <div className="flex gap-2">
              <CodeBlock value={inlineIframeCode} />
              <CopyButton text={inlineIframeCode} type="inlineIframe" copySuccess={copySuccess} onCopy={handleCopy} />
            </div>
          </div>

          <div className="space-y-2.5">
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Lightweight Snippet</h3>
              <span className="ml-auto text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">Recommended</span>
            </div>
            <div className="flex gap-2">
              <CodeBlock value={lightweightSnippet} />
              <CopyButton text={lightweightSnippet} type="lightweightSnippet" copySuccess={copySuccess} onCopy={handleCopy} />
            </div>
            <p className="text-xs text-muted-foreground">
              Paste before <code className="font-mono bg-muted px-1 rounded">&lt;/body&gt;</code>. A tiny loader
              (same pattern as widgets like Tawk.to) that mounts the floating launcher and resizes itself to match
              the widget exactly — a small button when closed, the full panel only while open — so it never blocks
              clicks or hover on the rest of your page. Fully responsive on desktop and mobile.
            </p>
          </div>

          <div className="space-y-2.5">
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Open It From Your Own Button</h3>
            </div>
            <div className="flex gap-2">
              <CodeBlock value={openApiExample} />
              <CopyButton text={openApiExample} type="openApi" copySuccess={copySuccess} onCopy={handleCopy} />
            </div>
            <p className="text-xs text-muted-foreground">
              Works with the Lightweight Snippet above. Use this for a promo popup, a hero CTA, or a
              &ldquo;talk to us&rdquo; link. The widget renders inside its own iframe, so trying to find and click the
              launcher with <code className="font-mono bg-muted px-1 rounded">document.querySelector</code> can never
              work — call these methods instead. Calling
              <code className="font-mono bg-muted px-1 rounded">open()</code> before the widget has loaded is fine; the
              command is queued and runs as soon as it is ready. Visitors land on the same screen a launcher click
              gives them.
            </p>
          </div>

          <div className="space-y-2.5">
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Floating Button (raw iframe)</h3>
            </div>
            <div className="flex gap-2">
              <CodeBlock value={floatingScriptCode} />
              <CopyButton text={floatingScriptCode} type="floatingScript" copySuccess={copySuccess} onCopy={handleCopy} />
            </div>
            <p className="text-xs text-muted-foreground">
              Fallback for pages where you cannot load an external script. Paste before
              <code className="font-mono bg-muted px-1 rounded">&lt;/body&gt;</code>.
              Requires <code className="font-mono bg-muted px-1 rounded">allow="microphone"</code> for voice calls.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Preview Modes</h3>
            <div className="grid grid-cols-3 gap-3">
              {[
                { view: 'button', label: 'Floating Button', desc: 'Launcher with first-open screen' },
                { view: 'inline', label: 'Inline Card', desc: 'Embedded in a page section' },
                { view: 'full', label: 'Full Page', desc: 'Standalone combo interface' },
              ].map(({ view, label, desc }) => (
                <button
                  key={view}
                  type="button"
                  onClick={() => openPreview(view)}
                  className="flex flex-col items-start gap-1 p-3 bg-muted/30 hover:bg-muted/60 border border-border rounded-xl text-left transition-colors"
                >
                  <span className="text-sm font-semibold text-foreground">{label}</span>
                  <span className="text-xs text-muted-foreground">{desc}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-border p-4 bg-muted/30 flex justify-between items-center flex-shrink-0">
          <button
            type="button"
            onClick={() => setShowCustomizer(true)}
            className="px-4 py-2 bg-gradient-button hover:opacity-90 text-primary-foreground rounded-lg flex items-center gap-2 text-sm"
          >
            <Palette className="w-4 h-4" />
            Customize Widget
          </button>
          <button type="button" onClick={onClose} className="px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg text-sm">
            Close
          </button>
        </div>
      </div>

      {showCustomizer && (
        <ComboWidgetV2ThemeCustomizer
          agent={agentState}
          onClose={() => setShowCustomizer(false)}
          onSave={(newSettings) => {
            setAgentState(prev => ({ ...prev, ...newSettings }));
            setShowCustomizer(false);
          }}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default ComboWidgetV2EmbedModal;
